import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../icon/icon';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { Stack } from '../../../stack/stack';
import { TextBox } from '../../../text-box/text-box';
import { numberOrDefault } from '../../asset-menu/utils/formatting';

type Props = {
  title: string;
  defaultValue: number;
  onValueChanged: (value: number) => void;
  onUseAutoValue: () => void;
};

const min = 0;
const max = 1000000;
const step = 0.01;

const isValidInput = (value?: string) => {
  const number = value ? parseFloat(value) : NaN;
  return !isNaN(number) && number >= min && number <= max - step;
};

const IntervalInput = React.memo((props: Props) => {
  const { t } = useTranslation();
  const ref = React.useRef<HTMLInputElement>(null);
  const [editable, setEditable] = React.useState(false);
  const [value, setValue] = React.useState<number | undefined>(props.defaultValue);
  const [valid, setIsValid] = React.useState(true);

  return (
    <LabelledContainer text={props.title}>
      <Stack alignItems="center" direction="row" spacing={0.5}>
        {editable ? (
          <TextBox
            errorMessage={valid ? undefined : ''}
            max={max}
            min={min}
            ref={ref}
            step={step}
            type="number"
            // If value is default value, then strip down to two decimals.
            // Otherwise, display value as is (to enable user to empty field, backspace etc)
            value={value === props.defaultValue ? value?.toFixed(2) : value?.toString()}
            onChange={(e) => {
              setValue(parseFloat(e.target.value) ?? undefined);

              if (isValidInput(e.target.value)) {
                let valueAsNumber = ref.current!.valueAsNumber;

                // If value (2 decimals) equals default value (2 decimals), then use default value
                // (that has more decimals) in order to have the same generated values (height/area)
                // in the map.
                //
                // The default value has higher precision than two decimals, but is displayed as two
                // decimals two the user. So if user enters the default value with two decimals,
                // the generated values in the map should be identical to the generated values using
                // the default value with higher precision.
                if (props.defaultValue.toFixed(2) === valueAsNumber.toFixed(2)) {
                  valueAsNumber = props.defaultValue;
                  setValue(valueAsNumber);
                }

                setIsValid(true);
                props.onValueChanged(valueAsNumber);
              } else {
                setIsValid(false);
              }
            }}
          />
        ) : (
          <TextBox
            readOnly={true}
            value={t('cutFillMap.interval.autoText', {
              ns: 'components',
              interval: numberOrDefault(props.defaultValue),
            })}
          />
        )}

        {editable ? (
          <Icon
            icon={['fad', 'circle-xmark']}
            title={t('cutFillMap.interval.useAuto', { ns: 'components' })}
            onClick={() => {
              setEditable(false);
              props.onUseAutoValue();
            }}
          />
        ) : (
          <Icon
            icon={['fad', 'pencil']}
            title={t('cutFillMap.interval.useCustom', { ns: 'components' })}
            onClick={() => {
              setEditable(true);

              if (isValidInput(value?.toString())) {
                props.onValueChanged(value!);
              }
            }}
          />
        )}
      </Stack>
    </LabelledContainer>
  );
});

IntervalInput.displayName = 'IntervalInput';

export { IntervalInput };
